import ManageAiVoicesModal from '@src/components/aiVoices/ManageAiVoicesModal';
import { useEnsureDubbingFields } from '@src/components/aiVoices/useEnsureDubbingFields';
import { useVoiceActivation } from '@src/components/aiVoices/useVoiceActivation';
import { ConfirmModal } from '@src/components/common/ConfirmModal';
import { OrbLoadingState } from '@src/components/common/OrbLoadingState';
import SpeakerFormModal from '@src/components/transcript/SpeakerFormModal';
import { useResolveOrgFromUrlParams } from '@src/hooks/useResolveOrgFromUrlParams';
import { useDatoPerson, useUserIdentity } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import SearchIcon from '@src/svgs/SearchIcon';
import { Studio } from '@src/types.ts/general';
import { checkWordsInString } from '@src/utility/general';
import { useFlagsCombination } from '@src/utility/useFlagsCombination';
import { observer } from 'mobx-react-lite';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import SpeakerListHeader, {
  type SortDir,
  type SortKey,
} from './SpeakerListHeader';
import SpeakerRow from './SpeakerRow';
import { useSpeakerManager } from './useSpeakerManager';

import type { SpeakerFormData } from './useSpeakerManager';
import type {
  DatoSpeaker,
  SpeakerStoryRef,
} from '@src/repositories/SpeakerRepository';

// Stable empty reference so speakers with no stories don't re-render each pass.
const NO_STORIES: SpeakerStoryRef[] = [];

type FormState =
  | { mode: 'create' }
  | { mode: 'edit'; speaker: DatoSpeaker }
  | null;

const SpeakerManager = observer(() => {
  const videoCreator = useVideoCreatorStore();
  const datoPerson = useDatoPerson();
  const userIdentity = useUserIdentity();
  // Resolve the org from a storyId/showcase deep-link/refresh when none is loaded.
  const resolvingOrg = useResolveOrgFromUrlParams();
  const album = datoPerson.selectedOrganization || videoCreator.organization;

  // "Manage AI Voices" is offered only when the AI Voices feature is on and
  // dubbing is enabled for the workspace (DE3/DE4).
  const { enableAiVoices } = useFlagsCombination(userIdentity.currentRole);
  // Hydrate the workspace dubbing activation + AI Voice catalog on load, so the
  // "AI Voices" column and the Manage AI Voices modal have data immediately —
  // without first visiting Settings → AI Voices. Idempotent per showcase; this
  // is the single hydration path (keyed to the same org `applyDubbingFields`
  // writes to) so the catalog and the dubbing fields never diverge.
  useEnsureDubbingFields();
  const { dubbingEnabled } = useVoiceActivation();
  const canManageAiVoices = enableAiVoices && dubbingEnabled;

  const {
    speakers,
    usage,
    loading,
    error,
    createSpeaker,
    updateSpeaker,
    removeSpeaker,
  } = useSpeakerManager(album?.id);

  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [formState, setFormState] = useState<FormState>(null);
  const [removeTarget, setRemoveTarget] = useState<DatoSpeaker | null>(null);
  const [removing, setRemoving] = useState(false);
  const [manageVoicesTarget, setManageVoicesTarget] =
    useState<DatoSpeaker | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set());
  const [selectedStoryId, setSelectedStoryId] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>('name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  // Clicking the active column flips direction; a new column starts ascending.
  const onSort = useCallback(
    (key: SortKey) => {
      if (sortKey === key) {
        setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortKey(key);
        setSortDir('asc');
      }
    },
    [sortKey],
  );

  const toggleExpanded = useCallback((speakerId: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(speakerId)) next.delete(speakerId);
      else next.add(speakerId);
      return next;
    });
  }, []);

  // Open a story in Content Studio (this route lives outside the Dashboard
  // tree, so we build the target URL directly rather than via useStoryNavigation).
  const openStory = useCallback(
    (storyId: string) => {
      const params = new URLSearchParams(window.location.search);
      navigate(
        `/${Studio.content}?storyId=${storyId}&env=${
          params.get('env') ?? ''
        }&playbackId=`,
      );
    },
    [navigate],
  );

  // Each visible row carries the clips (stories) to display beneath it: when a
  // search matches clip titles we narrow the sub-list to those matches, so the
  // query can surface a speaker via the clips underneath them, not just by name.
  const rows = useMemo(() => {
    const q = search.trim();
    const dir = sortDir === 'asc' ? 1 : -1;
    const valueFor = (s: DatoSpeaker) => {
      switch (sortKey) {
        case 'name':
          return s.name ?? '';
        case 'title':
          return s.title ?? '';
        case 'createdBy':
          return s.createdByUser?.name ?? '';
      }
    };

    const matched = speakers
      .map(s => {
        const stories = usage[s.id] ?? NO_STORIES;
        const speakerMatches =
          !q ||
          checkWordsInString(s.name, q) ||
          checkWordsInString(s.title ?? '', q);
        const matchingStories = q
          ? stories.filter(st => checkWordsInString(st.title ?? '', q))
          : stories;
        return { speaker: s, stories, speakerMatches, matchingStories };
      })
      .filter(r => r.speakerMatches || r.matchingStories.length > 0);

    // Blank cells sort last regardless of direction so populated rows lead.
    matched.sort((a, b) => {
      const av = valueFor(a.speaker);
      const bv = valueFor(b.speaker);
      if (!av && bv) return 1;
      if (av && !bv) return -1;
      return dir * av.localeCompare(bv, undefined, { sensitivity: 'base' });
    });

    return matched;
  }, [speakers, usage, search, sortKey, sortDir]);

  const handleSubmit = useCallback(
    async (data: SpeakerFormData): Promise<boolean> => {
      if (!formState) return false;
      if (formState.mode === 'create') return createSpeaker(data);
      return updateSpeaker(formState.speaker.id, data);
    },
    [formState, createSpeaker, updateSpeaker],
  );

  const handleConfirmRemove = useCallback(async () => {
    if (!removeTarget) return;
    setRemoving(true);
    const ok = await removeSpeaker(removeTarget.id);
    setRemoving(false);
    if (ok) {
      setRemoveTarget(null);
    } else {
      videoCreator.showToast({
        state: 'error',
        message: 'Failed to remove speaker',
      });
    }
  }, [removeTarget, removeSpeaker, videoCreator]);

  const renderContent = () => {
    // Same orb, same copy as the boot gate in EditorEntry: a refresh hands off
    // from that gate to this one, and a second indicator reads as a second wait
    // rather than the rest of the first. Regional, not the full-screen overlay —
    // this page is also reached by clicking Speaker Manager in the nav, where a
    // fixed overlay blacked out the nav and this page's own header for a wait
    // that is only the speaker list.
    if (resolvingOrg || (album?.id && loading)) {
      return <OrbLoadingState text="Loading…" />;
    }
    if (!album?.id) {
      return <Message>Select an organization to manage speakers.</Message>;
    }
    if (error) {
      return <Message>{error}</Message>;
    }
    if (speakers.length === 0) {
      return (
        <Message>
          No speakers yet. Add your first speaker to get started.
        </Message>
      );
    }
    return (
      <List>
        <SpeakerListHeader
          sortKey={sortKey}
          sortDir={sortDir}
          onSort={onSort}
          showAiVoices={canManageAiVoices}
        />
        {rows.length === 0 ? (
          <Message>No speakers match your search.</Message>
        ) : (
          rows.map(({ speaker: s, stories, matchingStories }) => {
            // When the search hit clip titles, show just those clips and force
            // the row open so the match is visible without a manual expand.
            const clipMatch =
              search.trim().length > 0 && matchingStories.length > 0;
            const shownStories = clipMatch ? matchingStories : stories;
            return (
              <SpeakerRow
                key={s.id}
                speaker={s}
                stories={shownStories}
                isExpanded={clipMatch || expandedIds.has(s.id)}
                onToggleExpand={() => toggleExpanded(s.id)}
                selectedStoryId={selectedStoryId}
                onSelectStory={setSelectedStoryId}
                onOpenStory={openStory}
                showAiVoices={canManageAiVoices}
                onEdit={() => setFormState({ mode: 'edit', speaker: s })}
                onRemove={() => setRemoveTarget(s)}
                onManageAiVoices={
                  canManageAiVoices ? () => setManageVoicesTarget(s) : undefined
                }
              />
            );
          })
        )}
      </List>
    );
  };

  return (
    <Main data-testid="speaker-manager-page">
      <TopContent>
        <TitleGroup>
          <Title>Speaker Manager</Title>
          <Subtitle>Manage your organization&rsquo;s speakers</Subtitle>
        </TitleGroup>
        <SearchWrapper>
          <SearchIcon fillColor="var(--text-muted)" />
          <SearchInput
            type="text"
            placeholder="Search speakers or clips"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {search && (
            <ClearButton onClick={() => setSearch('')}>&times;</ClearButton>
          )}
        </SearchWrapper>
        {album?.id && (
          <AddButton
            onClick={() => setFormState({ mode: 'create' })}
            data-testid="speaker-add-button"
          >
            Add Speaker
          </AddButton>
        )}
      </TopContent>

      <ContentWrapper>{renderContent()}</ContentWrapper>

      {formState && (
        <SpeakerFormModal
          isOpen
          onClose={() => setFormState(null)}
          onSubmit={handleSubmit}
          speakerToEdit={
            formState.mode === 'edit'
              ? {
                  speakerLabel: '',
                  datoSpeakerId: formState.speaker.id,
                  name: formState.speaker.name,
                  title: formState.speaker.title,
                  bio: formState.speaker.bio,
                  voice: formState.speaker.voice,
                  gender: formState.speaker.gender,
                  headshots: formState.speaker.headshots?.map(h => ({
                    id: h.id,
                    url: h.url,
                  })),
                }
              : undefined
          }
        />
      )}

      {removeTarget && (
        <ConfirmModal
          closeModal={() => {
            if (!removing) setRemoveTarget(null);
          }}
          onCancel={() => {
            if (!removing) setRemoveTarget(null);
          }}
          onConfirm={handleConfirmRemove}
          confirmText={removing ? 'Removing…' : 'Remove'}
          cancelText="Cancel"
          title={`Remove ${removeTarget.name} from this organization?`}
          message={
            <>
              They&rsquo;ll no longer appear in your organization&rsquo;s
              speaker library. This doesn&rsquo;t delete the speaker from
              stories that already use them.
            </>
          }
        />
      )}

      {manageVoicesTarget && (
        <ManageAiVoicesModal
          isOpen
          onClose={() => setManageVoicesTarget(null)}
          speakerId={manageVoicesTarget.id}
          speakerName={manageVoicesTarget.name}
          speakerHeadshotUrl={manageVoicesTarget.headshots?.[0]?.url}
        />
      )}
    </Main>
  );
});

export default SpeakerManager;

const Main = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  padding: var(--spacing-xl) var(--spacing-2xl) var(--spacing-md);
`;

const TopContent = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  min-width: 0;
`;

const TitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
  margin-right: auto;
`;

const Title = styled.h2`
  margin: 0;
  font-size: var(--text-xl);
  color: var(--foreground);
  font-weight: var(--font-weight-semi-bold);
`;

const Subtitle = styled.p`
  margin: 0;
  font-size: var(--text-sm);
  font-weight: var(--font-weight-normal);
  color: var(--text-secondary);
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  margin-top: var(--spacing-lg);
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
`;

const Message = styled.div`
  padding: var(--spacing-xl) 0;
  color: var(--text-muted);
  font-size: var(--text-sm);
`;

const SearchWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 320px;
  padding: 8px 12px;
  background-color: var(--input-background);
  border: 1px solid var(--input-border);
  border-radius: var(--radius-input);
  transition: all var(--transition-fast) ease;

  &:focus-within {
    border-color: var(--text-muted);
  }
`;

const SearchInput = styled.input`
  flex: 1;
  background: none;
  border: none;
  outline: none;
  color: var(--text-secondary);
  font-family: var(--font-primary);
  font-size: var(--text-sm);
  font-weight: var(--font-weight-normal);

  &::placeholder {
    color: var(--text-muted);
  }
`;

const ClearButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: var(--text-muted);
  font-size: 16px;
  line-height: 1;
  display: flex;
  align-items: center;

  &:hover {
    color: var(--text-secondary);
  }
`;

const AddButton = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  height: 40px;
  width: auto;
  min-width: 140px;
  flex-shrink: 0;
  padding: 0 16px;
  border-radius: 8px;
  background: var(--primary);
  color: var(--background);
  border: 1px solid var(--primary);
  font-size: 14px;
  font-weight: 700;
  line-height: normal;
  cursor: pointer;

  &:hover {
    opacity: 0.9;
  }
`;
